/**
 * Owner: Haselton Baker Risk Group, LLC
 * Product: SP3 - SP3-Ui
 * Developer: Mike McGlone <mike@hbrisk.com>
 * Date: 11/21/17
 * Copyright All Rights Reserved
 */

import { connect } from 'react-redux';
import { change, reduxForm, reset, SubmissionError } from 'redux-form';
import { createSite } from 'actions/siteCreators';
import SiteInfoForm from 'components/forms/SiteInfoForm';
import { currentReportIsSaving, currentReportError } from 'selectors/siteSelectors';
import { getToken, getUser } from 'selectors/tokenSelectors';
import store from 'store';
import { mapTypes } from 'components/forms/ZoomLevelSelectMenu';
import { SECTIONS, sectionFormat } from 'components/forms/ReportSectionsToInclude';

// In this module, we first wrap SiteInfoFormComponent in a Redux Form container, adding
// Redux Form functionality to that underlying component. Then, after creating some helpers, we use
// React-Redux connect() to provide further mappings of Redux state and dispatch to the underlying
// forms props. Finally we export the result of adding these two additional layers

const zoomInitialValues = mapTypes.reduce((acc, curr) => ({
  ...acc,
  [curr.inputName]: curr.default,
}), {});

const sectionsToIncludeValues = SECTIONS.reduce((acc, curr) => ({
  ...acc,
  [sectionFormat(curr)]: curr.value !== undefined ? curr.value : true,
}), {});

const initialValues = {
  name: '',
  autocomplete: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  lat: '',
  lng: '',
  ...zoomInitialValues,
  ...sectionsToIncludeValues,
  siteClass: 'usgs',
  shouldRedirect: false,
};

export const getZoomValues = values => mapTypes.reduce((acc, curr) => ({
  ...acc,
  [curr.name]: parseInt(values[curr.inputName], 10),
}), {});

export const getSectionValues = values => SECTIONS.reduce((acc, curr) => {
  const val = values[sectionFormat(curr)];
  return {
    ...acc,
    [curr.name]: val === '' ? false : val,
  };
}, {});

const createRequestBody = values => ({
  name: values.name,
  address: {
    street: values.street,
    city: values.city,
    state: values.state,
    zip: values.zip,
    lat: parseFloat(values.lat),
    lng: parseFloat(values.lng),
  },
  options: {
    siteClass: values.siteClass,
    zoom: getZoomValues(values),
    reportSections: getSectionValues(values),
  },
});

const onSubmit = (values, dispatch, props) => {
  const body = createRequestBody(values);

  // dispatch an action for submitting form data to server
  dispatch(createSite(body, props.userId, props.token));

  // return a promise that subscribes to redux state, listening
  // for updates to state based on the response and resolving or rejecting
  // in response to state updates
  return new Promise((resolve, reject) => {
    const unsubscribe = store.subscribe(() => {
      const state = store.getState();
      if (!currentReportIsSaving(state)) {
        unsubscribe();
        if (currentReportError(state)) {
          reject(new SubmissionError({
            _error: currentReportError(state).message,
          }));
        } else {
          dispatch(change('siteInfo', 'shouldRedirect', true));
          dispatch(reset('siteInfo'));
          resolve();
        }
      }
    });
  });
};

// Wrap SiteInfoFormComponent in a Redux Form container
const SiteInfoFormContainer = reduxForm({
  form: 'siteInfo',
  initialValues,
  onSubmit,
})(SiteInfoForm);

// Create helpers for defining mapStateToProps and mapDispatchToProps

/**
 * Extracts and (an optionally parses) a component of Redux store's representation of a form field's
 * value
 *
 * @param state
 * @param field
 * @returns {string}
 */
const extractState = (state, field) => (
  state.form.siteInfo === undefined || state.form.siteInfo.values === undefined ?
  initialValues[field] : state.form.siteInfo.values[field]
);

/**
 * Given an array of addressComponents and an address component type, returns the
 * address component with that type.
 * @param type,
 * @param addressComponents
 */
const findComponentByType = (type, addressComponents) => {
  const filtered = addressComponents.filter(
    component => type === component.types[0]
  );
  return filtered.length === 0 ? null : filtered[0];
};

/**
 * Given an array of addressComponents, calculate the full street address
 * @param addressComponents
 */
const calculateStreetAddress = (addressComponents) => {
  const streetNumber = findComponentByType('street_number', addressComponents);
  const streetName = findComponentByType('route', addressComponents);
  return streetNumber && streetNumber.short_name
    && streetName && streetName.short_name ? `${streetNumber.short_name} ${streetName.short_name}` : '';
};

/**
 * Given array of addressComponents and a type, return the address component with that
 * type
 *
 * @param type
 * @param addressComponents
 */
const getComponentValueByType = (type, addressComponents) => {
  const component = findComponentByType(type, addressComponents);
  return component && component.short_name ? component.short_name : '';
};

/**
 * From a collection of address components, get the value for the "city" component
 *
 * Note: there is no actual "city" type in a collection of address components. Instead, there are
 * a list of proxy types (including 'locality', 'sublocality_level_1', et al.) that are used here
 * to provide a value for "city".
 *
 * @param addressComponents
 * @returns {string} - the name of a city or '' if none is available
 *
 */
const getCity = (addressComponents) => {
  const types = ['locality', 'sublocality_level_1'];
  return types.reduce(
    (acc, type) => (acc === '' ? getComponentValueByType(type, addressComponents) : acc),
    ''
  );
};

// Set up for applying connect() by defining mapStateToProps and mapDispatchToProps

const mapStateToProps = state => (
  {
    lat: extractState(state, 'lat').toString(),
    lng: extractState(state, 'lng').toString(),
    shouldRedirect: extractState(state, 'shouldRedirect'),
    token: getToken(state),
    userId: getUser(state),
  }
);

const mapDispatchToProps = dispatch => ({
  /**
   * Use the auto-complete data to update the Redux store's values for the address form fields
   *
   * Return true if at least one field is updated with a non-empty value; Return false otherwise;
   */
  updateLocationFields: (autocomplete) => {
    let changes;
    try {
      const place = autocomplete.getPlace();
      const addressComponents = place.address_components;
      const location = place.geometry.location;
      const formattedAddress = place.formatted_address;
      changes = {
        autocomplete: formattedAddress,
        street: calculateStreetAddress(addressComponents),
        city: getCity(addressComponents),
        state: getComponentValueByType('administrative_area_level_1', addressComponents),
        zip: getComponentValueByType('postal_code', addressComponents),
        lat: location.lat().toString(),
        lng: location.lng().toString(),
      };
    } catch (e) {
      changes = {
        street: '',
        city: '',
        state: '',
        zip: '',
        lat: '',
        lng: '',
      };
    }

    const fields = Object.keys(changes);
    fields.forEach((field) => {
      if (changes[field] !== undefined) {
        dispatch(change('siteInfo', field, changes[field]));
      }
    });
    return fields.some(field => changes[field] !== '');
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SiteInfoFormContainer);
